import { FeatureFlag } from '@ps/web-core-enums';

import type { RootState, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';

import type { GameTypeName } from '../types/Game';
import { isGameOfType } from '../utils';
import getHighestValueGame from '../utils/getHighestValueGame';
import getTopPrizeGame from '../utils/getTopPrizeGame';

import type { CommonRequestOptions, PokerSpinAndGoState } from './types';

export const selectors = (slice: Slicer<PokerSpinAndGoState>, createSelector: SelectorCreator) => ({
  hasPreloaded: () => slice((state) => state.hasPreloaded),
  isLoading: () => slice((state) => state.isLoading),
  hasError: () => slice((state) => state.hasError),
  games: () => slice((state) => state.games),
  watchGames: () => slice((state) => state.watchGames),
  tickerMessage: () => slice((state) => state.tickerMessage),
  userImage: () =>
    createSelector(
      slice,
      (_: RootState, imageId: number) => imageId,
      ({ watchGames }, imageId) => watchGames.userImageMap?.[imageId] || ''
    ),
  highestValueGame: () =>
    createSelector(
      slice,
      (_: RootState, gameType: GameTypeName) => gameType,
      ({ games }, gameType) =>
        getHighestValueGame(games.filter((game) => isGameOfType(game, gameType)))
    ),
  topPrizeGame: () =>
    createSelector(
      slice,
      (_: RootState, gameType: GameTypeName) => gameType,
      ({ games }, gameType) => getTopPrizeGame(games.filter((game) => isGameOfType(game, gameType)))
    ),
  getRequestData() {
    return createSelector(
      slice,
      (state: RootState): CommonRequestOptions => ({
        locale: select.locale.get(state),
        isPlayMoney: select.currency.isPlayMoney(state),
        site: select.cms.getSiteId(state),
        brand: select.cms.getBrandId(state),
        country: select.account.getCountry(state) ?? select.country.getCountry(state)
      }),
      (_, data) => data
    );
  },
  isSharedLiquidityAvailable() {
    return createSelector(
      slice,
      (state: RootState) => ({
        isAgeVerified: select.account.isAgeVerified(state),
        isAuthVerified: select.account.isAuthVerified(state),
        isPlayMoney: select.currency.isPlayMoney(state),
        isAuthenticated: select.session.isAuthenticated(state),
        isSharedLiquidityEnabled: select.feature.check(
          state,
          FeatureFlag.PKRRegulatoryAuthAgeVerification
        )
      }),
      (_, variables) => {
        const {
          isAgeVerified,
          isAuthenticated,
          isAuthVerified,
          isPlayMoney,
          isSharedLiquidityEnabled
        } = variables;

        return (
          !isSharedLiquidityEnabled ||
          !isAuthenticated ||
          isPlayMoney ||
          (isAuthVerified && isAgeVerified)
        );
      }
    );
  }
});
